/**
 * G2eGente - Modal de Notificação de Solicitações Pendentes
 * Exibido ao aprovador quando há solicitações aguardando aprovação
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { changeRequestsAPI } from '../lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, Clock, Briefcase, DollarSign, UserPlus, UserMinus } from 'lucide-react';

const TIPOS_ICONS = {
  mudanca_cargo: Briefcase,
  alteracao_salarial: DollarSign,
  admissao: UserPlus,
  demissao: UserMinus
};

const TIPOS_LABELS = {
  mudanca_cargo: 'Mudança de Cargo',
  alteracao_salarial: 'Alteração Salarial',
  admissao: 'Admissão',
  demissao: 'Demissão'
};

export default function PendingApprovalsModal({ user }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Verifica se o usuário é COMPANY e se ainda não foi dispensado nesta sessão
    if (user?.role === 'COMPANY' && !dismissed) {
      checkPendingApprovals();
    }
  }, [user, dismissed]);

  const checkPendingApprovals = async () => {
    try {
      // Verificar se já foi mostrado nesta sessão
      const sessionKey = `pendingApprovalsShown_${user?.id}_${new Date().toDateString()}`;
      if (sessionStorage.getItem(sessionKey)) {
        return;
      }

      const res = await changeRequestsAPI.getPendingCount();
      
      if (res.data.is_aprovador && res.data.count > 0) {
        setPendingData(res.data);
        setIsOpen(true);
        // Marcar como mostrado nesta sessão
        sessionStorage.setItem(sessionKey, 'true');
      }
    } catch (error) {
      // Silenciosamente falhar - não é crítico
      console.log('Erro ao verificar pendências:', error);
    }
  };

  const handleViewRequests = () => {
    setIsOpen(false);
    navigate('/company/gestao-atividades');
  };

  const handleDismiss = () => {
    setIsOpen(false);
    setDismissed(true);
  };

  const calcularDiasAtras = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'hoje';
    if (diff === 1) return 'há 1 dia';
    return `há ${diff} dias`;
  };

  if (!pendingData || pendingData.count === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <DialogTitle>Solicitações Aguardando Aprovação</DialogTitle>
              <DialogDescription>
                Você possui {pendingData.count} solicitaç{pendingData.count === 1 ? 'ão' : 'ões'} pendente{pendingData.count === 1 ? '' : 's'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="py-4">
          <div className="space-y-2 max-h-[200px] overflow-y-auto">
            {pendingData.solicitacoes?.slice(0, 5).map((sol) => {
              const Icon = TIPOS_ICONS[sol.tipo] || Clock;
              return (
                <div 
                  key={sol.id} 
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-slate-500" />
                    <div>
                      <p className="text-sm font-medium">
                        {TIPOS_LABELS[sol.tipo] || sol.tipo}
                      </p>
                      <p className="text-xs text-slate-500">
                        {sol.funcionario_nome || 'Novo funcionário'}
                      </p>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {calcularDiasAtras(sol.created_at)}
                  </Badge>
                </div>
              );
            })}
            {pendingData.count > 5 && (
              <p className="text-xs text-center text-slate-500 pt-2">
                E mais {pendingData.count - 5} solicitaç{pendingData.count - 5 === 1 ? 'ão' : 'ões'}...
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleDismiss}>
            Lembrar depois
          </Button>
          <Button onClick={handleViewRequests} className="bg-amber-600 hover:bg-amber-700">
            Ver Solicitações
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
